import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { IPricing } from "@/app/domain";
import { Pricing } from "@/app/domain";
import { useSpacesStore } from "@/stores/new/spaces.store";
import { EstimateItemCreationStrategy } from "@/app/domain/strategies/creation/estimate-item-creation.strategy";
import { EstimateItemTotalCreationStrategy } from "@/app/domain/strategies/creation/estimate-item-total-creation.strategy";
import { EstimateItem } from "@/app/domain/entities/estimate";
import { PricingService } from "@/app/services/pricing.service";

export const useEstimateReviewStore = defineStore(
    "estimate-review-store",
    () => {
        const spacesStore = useSpacesStore();
        const pricingService = new PricingService();

        const spaces = computed(() => spacesStore.spaces);

        const subtotalPrice = ref<IPricing>();
        const totalDue = ref<IPricing>();
        const materialPriceSum = ref<IPricing>();
        const estimateItemList = ref<EstimateItem[]>([]);
        const estimateTotal = ref<EstimateItem | null>(null);

        const createEstimateItemList = () => {
            spaces.value.forEach((space) => {
                space.getAllPartitions().forEach((partition) => {
                    const strategy = new EstimateItemCreationStrategy(partition);
                    const estimateItem = strategy.create() as EstimateItem;

                    // Check is it a new item or update existing one
                    const existingIndex = estimateItemList.value.findIndex((item) =>
                        item.parentId === estimateItem.parentId.value
                    );

                    if (existingIndex == -1) {
                        estimateItemList.value.push(estimateItem);
                        return
                    }

                    estimateItemList.value[existingIndex] = estimateItem;
                });
            });
        };

        const updatePartitionsTableTotal = () => {
            const strategy = new EstimateItemTotalCreationStrategy(estimateItemList.value);
            const estimateItemTotal = strategy.create();
            estimateTotal.value = estimateItemTotal as EstimateItem;
        }

        const calculateServiceSum = () => {
            const servicesPrice = pricingService.calculateServicesSumWithDiscount();

            return new Pricing(servicesPrice.price, servicesPrice.priceB2C, servicesPrice.priceB2B);
        }

        const calculateOverheadSum = () => {
            const overheadPrice = pricingService.calculateOverheadSumWithDiscount();

            return new Pricing(overheadPrice.price, overheadPrice.priceB2C, overheadPrice.priceB2B);
        }

        const $reset = () => {
            subtotalPrice.value = null;
            totalDue.value = null;
            materialPriceSum.value = null;
            estimateTotal.value = null;
            estimateItemList.value = [];
        };


        return {
            subtotalPrice,
            totalDue,
            createEstimateItemList,
            materialPriceSum,
            estimateItemList,
            calculateServiceSum,
            calculateOverheadSum,
            updatePartitionsTableTotal,
            estimateTotal,
            $reset,
        };
    },
    { persist: true },
);
